/**
 * Validation Service
 * Contains reusable validation functions for form inputs
 */

// SSN Validation
export const validateSSN = {
  format: (value) => {
    // Handle when value is not a string
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove all non-digits first
    const digits = valueStr.replace(/\D/g, '');
    
    // Format with dashes as user types (e.g., XXX-XX-XXXX)
    if (digits.length > 0) {
      if (digits.length < 4) {
        return digits;
      } else if (digits.length < 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
      }
    }
    return '';
  },
  
  mask: (event) => {
    // Only allow digits and dashes
    const regex = /^[\d-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Check if input contains any non-digit characters (excluding dashes)
    const invalidChars = valueStr.replace(/-/g, '').replace(/\d/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'SSN can only contain numbers and dashes'
      };
    }
    
    // Remove dashes for validation
    const digits = valueStr.replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digits.length > 0 && digits.length < 9) {
      return {
        isValid: false,
        message: 'SSN must be 9 digits'
      };
    }
    
    // Check if it's exactly 9 digits
    if (digits.length !== 9) {
      return {
        isValid: false,
        message: 'Social Security Number must be 9 digits'
      };
    }
    
    // Extract the three parts of the SSN
    const area = digits.substring(0, 3);
    const group = digits.substring(3, 5);
    const serial = digits.substring(5, 9);
    
    // Check for invalid area codes
    if (area === '000') {
      return {
        isValid: false,
        message: 'SSN area code cannot be 000'
      };
    }
    
    if (area === '666') {
      return {
        isValid: false,
        message: 'SSN area code 666 is not valid'
      };
    }
    
    // Check for invalid group codes
    if (group === '00') {
      return {
        isValid: false,
        message: 'SSN group code cannot be 00'
      };
    }
    
    // Check for invalid serial numbers
    if (serial === '0000') {
      return {
        isValid: false,
        message: 'SSN serial number cannot be 0000'
      };
    }
    
    // Check for obviously invalid SSNs (000-00-0000, 123-45-6789, etc.)
    if (/^123-?45-?6789$/.test(valueStr)) {
      return {
        isValid: false,
        message: 'Please enter a valid SSN, not a placeholder'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // Function to normalize before sending to backend
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    const valueStr = String(value);
    return valueStr.replace(/\D/g, '');
  }
};

// Phone Number Validation
export const validatePhone = {
  format: (value) => {
    // Handle when value is not a string
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Check if there's a +1 prefix that we want to preserve
    const hasCountryCode = valueStr.startsWith('+1');
    
    // Remove all non-digits (keeping the +1 prefix if it exists)
    const prefix = hasCountryCode ? '+1' : '';
    const digitsOnly = valueStr.replace(/^\+1/, '').replace(/\D/g, '');
    
    // Format as user types, preserving the +1 prefix if it exists
    if (digitsOnly.length > 0) {
      if (digitsOnly.length < 4) {
        return `${prefix}(${digitsOnly}`;
      } else if (digitsOnly.length < 7) {
        return `${prefix}(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
      } else {
        return `${prefix}(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      }
    }
    
    return prefix; // Return just the prefix if no digits
  },
  
  mask: (event) => {
    // Allow + only at the beginning of the input
    if (event.key === '+' && event.target.selectionStart !== 0) {
      event.preventDefault();
      return;
    }
    
    // Only allow digits and formatting characters
    const regex = /^[\d\(\)\s\-\+]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Check if input contains any non-digit characters (excluding formatting chars and +)
    const invalidChars = valueStr.replace(/[\d\(\)\s\-\+]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'Phone number can only contain numbers and formatting characters'
      };
    }
    
    // Check if + is used anywhere except the beginning
    if (valueStr.includes('+') && !valueStr.startsWith('+')) {
      return {
        isValid: false,
        message: 'The + symbol can only be used at the beginning of the phone number'
      };
    }
    
    // Ensure proper +1 country code format if + is used
    if (valueStr.startsWith('+') && !valueStr.startsWith('+1')) {
      return {
        isValid: false,
        message: 'International format should be +1 followed by a 10-digit number'
      };
    }
    
    // Remove country code and non-digits for validation
    const digitsOnly = valueStr.replace(/^\+1/, '').replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digitsOnly.length > 0 && digitsOnly.length < 10) {
      return {
        isValid: false,
        message: 'Phone number must be 10 digits'
      };
    }
    
    // Check if it's exactly 10 digits (US phone number)
    if (digitsOnly.length !== 10) {
      return {
        isValid: false,
        message: 'Phone number must be 10 digits'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    const valueStr = String(value);
    
    // Preserve the +1 if it exists, but only store the digits for the database
    const hasCountryCode = valueStr.startsWith('+1');
    if (hasCountryCode) {
      // Return the phone number with the +1 prefix
      return '+1' + valueStr.replace(/^\+1/, '').replace(/\D/g, '');
    } else {
      // Return just the digits
      return valueStr.replace(/\D/g, '');
    }
  }
};

// Email Validation
export const validateEmail = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    
    if (!regex.test(valueStr)) {
      return {
        isValid: false,
        message: 'Please enter a valid email address'
      };
    }
    
    return { isValid: true, message: '' };
  }
};

// Currency Validation
export const validateCurrency = {
  format: (value) => {
    // Handle when value is not a string
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove non-digits/dots
    let sanitized = valueStr.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Format with commas
    const numberParts = sanitized.split('.');
    numberParts[0] = numberParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return numberParts.join('.');
  },
  
  mask: (event) => {
    // Allow only digits, commas, and one decimal point
    const value = event.target.value;
    const decimalCount = (value.match(/\./g) || []).length;
    
    if (!/^[\d,.]$/.test(event.key) && 
        event.key !== 'Backspace' && 
        event.key !== 'Tab') {
      event.preventDefault();
    }
    
    // Prevent additional decimal points if one already exists
    if (event.key === '.' && decimalCount >= 1) {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Convert to string if it's a number
    const valueStr = String(value);
    
    // Check for invalid characters
    const invalidChars = valueStr.replace(/[\d,.\$]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'Amount can only contain numbers, commas, and decimal points'
      };
    }
    
    // Remove commas for validation
    const sanitized = valueStr.replace(/,/g, '');
    
    if (isNaN(parseFloat(sanitized))) {
      return {
        isValid: false,
        message: 'Please enter a valid amount'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    // Handle non-string values
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    return parseFloat(valueStr.replace(/,/g, '')).toString();
  }
};

// ZIP Code Validation
export const validateZip = {
  format: (value) => {
    // Handle when value is not a string
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove non-digits and hyphens
    const digits = valueStr.replace(/[^\d-]/g, '');
    
    // Format as XXXXX or XXXXX-XXXX
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
    
    return digits;
  },
  
  mask: (event) => {
    // Only allow digits and dash
    const regex = /^[\d-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Check if input contains any non-digit characters (excluding dash)
    const invalidChars = valueStr.replace(/[\d-]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'ZIP code can only contain numbers and a dash'
      };
    }
    
    // Remove dash for validation
    const digits = valueStr.replace(/\D/g, '');
    
    // Check for valid ZIP code formats (5 digits or 5+4 digits)
    if (digits.length !== 5 && digits.length !== 9) {
      return {
        isValid: false,
        message: 'ZIP code must be 5 digits or 9 digits (ZIP+4)'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    const valueStr = String(value);
    return valueStr.replace(/\D/g, '');
  }
};

// EIN Validation (Employer Identification Number)
export const validateEIN = {
  format: (value) => {
    // Handle when value is not a string
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove all non-digits first
    const digits = valueStr.replace(/\D/g, '');
    
    // Format with a dash (XX-XXXXXXX)
    if (digits.length > 2) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
    }
    
    return digits;
  },
  
  mask: (event) => {
    // Only allow digits and dash
    const regex = /^[\d-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Check if input contains any non-digit characters (excluding dash)
    const invalidChars = valueStr.replace(/[\d-]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'EIN can only contain numbers and a dash'
      };
    }
    
    // Remove dash for validation
    const digits = valueStr.replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digits.length > 0 && digits.length < 9) {
      return {
        isValid: false,
        message: 'EIN must be 9 digits'
      };
    }
    
    // Check if it's exactly 9 digits
    if (digits.length !== 9) {
      return {
        isValid: false,
        message: 'EIN must be 9 digits (XX-XXXXXXX)'
      };
    }
    
    // Check for obviously invalid EINs
    if (/^0{9}$/.test(digits) || /^1{9}$/.test(digits)) {
      return {
        isValid: false,
        message: 'Please enter a valid EIN'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    const valueStr = String(value);
    return valueStr.replace(/\D/g, '');
  }
};

// Date of Birth Validation
export const validateDateOfBirth = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string for date parsing
    const valueStr = String(value);
    
    // Convert to Date object
    const birthDate = new Date(valueStr);
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return {
        isValid: false,
        message: 'Please enter a valid date'
      };
    }
    
    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Check if at least 18 years old
    if (age < 18) {
      return {
        isValid: false,
        message: 'Contact must be at least 18 years old'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // Format the date in MM/DD/YYYY format
  format: (value) => {
    if (value === undefined || value === null) return '';
    return String(value);
  },
  
  // Normalize the date to ISO format for backend
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    // Ensure we're working with a string
    const valueStr = String(value);
    const date = new Date(valueStr);
    if (isNaN(date.getTime())) return valueStr;
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  }
};

// Percentage Validation (for Ownership Percentage)
export const validatePercentage = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Convert to string if number provided
    const valueStr = String(value);
    
    // Remove any % sign if present
    const cleanValue = valueStr.replace(/%/g, '').trim();
    
    // Check if it's a valid number
    if (isNaN(parseFloat(cleanValue))) {
      return {
        isValid: false,
        message: 'Percentage must be a valid number'
      };
    }
    
    // Parse the value as a number
    const percentValue = parseFloat(cleanValue);
    
    // Check if it's within the valid range (0-100)
    if (percentValue < 0 || percentValue > 100) {
      return {
        isValid: false,
        message: 'Percentage must be between 0 and 100'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  format: (value) => {
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove any existing % signs first
    let cleanValue = valueStr.replace(/%/g, '');
    
    // Format to at most 2 decimal places
    if (cleanValue !== '' && !isNaN(parseFloat(cleanValue))) {
      const num = parseFloat(cleanValue);
      // Return formatted percentage (without % sign for form field)
      return num.toFixed(Math.min(2, (cleanValue.split('.')[1] || '').length));
    }
    
    return cleanValue;
  },
  
  mask: (event) => {
    // Allow only digits, decimal point, and percent sign
    const regex = /^[\d.%]$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
    
    // Prevent multiple decimal points
    if (event.key === '.' && event.target.value.includes('.')) {
      event.preventDefault();
    }
  },
  
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    
    // Ensure we're working with a string
    const valueStr = String(value);
    
    // Remove any % sign and convert to a number
    const cleanValue = valueStr.replace(/%/g, '').trim();
    
    if (cleanValue === '' || isNaN(parseFloat(cleanValue))) {
      return '';
    }
    
    // Return as a number
    return parseFloat(cleanValue).toString();
  }
};

// Street Address Validation
export const validateStreet = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (value === undefined || value === null || value === '') {
      return { isValid: true, message: '' };
    }
    
    // Ensure we're working with a string
    const valueStr = String(value).trim();
    
    // Check minimum length
    if (valueStr.length < 5) {
      return {
        isValid: false,
        message: 'Address appears too short'
      };
    }
    
    // Check if address starts with a number (required)
    if (!/^\d+/.test(valueStr)) {
      return {
        isValid: false,
        message: 'Address should start with a street number'
      };
    }
    
    // List of common USPS street suffixes (case insensitive)
    const validSuffixes = [
      'st', 'street', 'ave', 'avenue', 'blvd', 'boulevard', 'dr', 'drive', 
      'ct', 'court', 'pl', 'place', 'rd', 'road', 'ln', 'lane', 'way', 
      'cir', 'circle', 'trce', 'trace', 'trl', 'trail', 'ter', 'terrace', 
      'pkwy', 'parkway', 'hwy', 'highway', 'sq', 'square', 'aly', 'alley',
      'holw', 'hollow', 'bnd', 'bend', 'xing', 'crossing', 'pass', 'crst', 
      'crest', 'vw', 'view', 'brg', 'bridge', 'cv', 'cove', 'lk', 'lake',
      'vlg', 'village', 'grv', 'grove', 'grn', 'green', 'gln', 'glen',
      'fwy', 'freeway', 'expwy', 'expressway', 'ext', 'extension',
      'crk', 'creek', 'cswy', 'causeway', 'frst', 'forest', 'loop',
      'mdw', 'meadow', 'mdws', 'meadows', 'mnr', 'manor', 'mnt', 'mount',
      'opas', 'overpass', 'pnes', 'pines', 'plz', 'plaza', 'pt', 'point',
      'vly', 'valley', 'rnch', 'ranch', 'strm', 'stream', 'spr', 'spring',
      'spgs', 'springs', 'stn', 'station'
    ];
    
    // Split address by spaces to extract components
    const addressParts = valueStr.split(' ');
    
    // Need at least a number and street name
    if (addressParts.length < 2) {
      return {
        isValid: false,
        message: 'Address should include number and street name'
      };
    }
    
    // Extract first part (should be the street number)
    const streetNumber = addressParts[0];
    
    // Check if street number looks reasonable
    if (!/^\d+$/.test(streetNumber) || parseInt(streetNumber) === 0) {
      return {
        isValid: false,
        message: 'Address should start with a valid street number'
      };
    }
    
    // Check if there's text after the number that can be a street name
    if (addressParts.length === 2 && addressParts[1].length < 3) {
      return {
        isValid: false,
        message: 'Address should include a complete street name'
      };
    }
    
    // Look for a suffix in a case-insensitive way
    let hasSuffix = false;
    
    for (let i = 1; i < addressParts.length; i++) {
      if (validSuffixes.includes(addressParts[i].toLowerCase())) {
        hasSuffix = true;
        break;
      }
    }
    
    // Allow addresses with more parts even without a recognized suffix
    // This accommodates many address formats we may not explicitly recognize
    if (!hasSuffix && addressParts.length >= 3) {
      // If it has at least 3 parts, it might have an unrecognized suffix
      // or be a complex address format with unit, apartment, suite, etc.
      hasSuffix = true;
    }
    
    if (!hasSuffix) {
      return {
        isValid: false,
        message: 'Address should include street name and suffix'
      };
    }
    
    // Return success if we made it this far
    return { isValid: true, message: '' };
  },
  
  // Format is not necessary as street addresses have varied formats
  format: (value) => {
    if (value === undefined || value === null) return '';
    return String(value);
  },
  
  // Normalize for backend by trimming extra spaces
  normalize: (value) => {
    if (value === undefined || value === null) return '';
    return String(value).trim().replace(/\s+/g, ' ');
  }
};

// State Validation (for dropdown state fields)
export const validateState = {
  validate: (value) => {
    // If empty, undefined, null, or still showing default "Select State..." option
    if (value === undefined || value === null || value === '' || 
        (typeof value === 'string' && value.trim() === '')) {
      return {
        isValid: false,
        message: 'Please select a state'
      };
    }
    
    // List of valid US state codes
    const validStateCodes = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
      'DC', 'AS', 'GU', 'MP', 'PR', 'VI'
    ];
    
    // Ensure we're working with a string and uppercase it
    const valueStr = String(value).toUpperCase().trim();
    
    // Check if it's a valid state code
    if (!validStateCodes.includes(valueStr)) {
      return {
        isValid: false,
        message: 'Please select a valid state'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // No special formatting needed for state dropdowns
  format: (value) => {
    if (value === undefined || value === null) return '';
    return String(value).toUpperCase();
  },
  
  // Normalize to uppercase for consistency
  normalize: (value) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value).toUpperCase();
  }
};

// Entity Type Validation (for dropdown entity type fields)
export const validateEntityType = {
  validate: (value) => {
    // If empty, undefined, null, or still showing default option
    if (value === undefined || value === null || value === '' || 
        (typeof value === 'string' && value.trim() === '')) {
      return {
        isValid: false,
        message: 'Please select an entity type'
      };
    }
    
    // List of valid entity types
    const validEntityTypes = ['LLC', 'Partnership', 'Sole Proprietor', 'Corporation'];
    
    // Check if it's a valid entity type
    if (!validEntityTypes.includes(value)) {
      return {
        isValid: false,
        message: 'Please select a valid entity type'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // No special formatting needed
  format: (value) => {
    if (value === undefined || value === null) return '';
    return String(value);
  },
  
  // No special normalization needed
  normalize: (value) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value);
  }
};

// Allow this list to grow as needed by adding new validation functions
// When adding new validators, follow the pattern of including:
// - format: function to format input as user types
// - mask: function to restrict input characters
// - validate: function to check if the final value is valid
// - normalize: function to convert to the format needed for backend 